import { useHeaderPanels } from '@/components/content/Header/context/HeaderPanelsContext';
import { AccountDropMenuPanel } from '@/components/content/Header/parts/HDMAccountPanel/AccountDropMenuPanel';
import { HeaderCommonPanel } from '@/components/content/Header/parts/HeaderCommonPanel';
import { accountItemSX, accountSX } from '@/components/content/Header/styles/account/account';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useLocalization } from '@/data/Localization';
import { Box, Stack, Typography } from '@mui/material';
import { Breakpoint, useTheme } from '@mui/material/styles';
import { FC, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS, OPEN_CLOSE_STATES } from 'shared/custom/constants/commonConstants';

type Props = {
	mobileBreakpoint: Breakpoint;
};

export const HeaderAccount: FC<Props> = () => {
	const idPopper = 'accountPanel';
	const { activePanelId, anchorEl, togglePanel, closePanel } = useHeaderPanels();
	const isPanelOpen = activePanelId === idPopper;
	const [isHovered, setIsHovered] = useState(false);
	const isActive = isPanelOpen || isHovered;

	const HeaderLabels = useLocalization('Header');
	const theme = useTheme();

	const handlePanelStatus = (
		event:
			| React.MouseEvent<HTMLElement>
			| React.KeyboardEvent<HTMLDivElement>
			| React.FocusEvent<HTMLDivElement, Element>
			| null,
		action?: OPEN_CLOSE_STATES
	) => {
		if (action === OPEN_CLOSE_STATES.OPEN && isPanelOpen) {
			closePanel();
			return;
		}
		if (activePanelId === null) togglePanel(idPopper, event?.currentTarget ?? null);
	};

	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handlePanelStatus(event, OPEN_CLOSE_STATES.OPEN);
		}
	};

	return (
		<Box>
			<Stack
				aria-describedby="account-panel"
				aria-expanded={isPanelOpen}
				onClick={(e) => handlePanelStatus(e, OPEN_CLOSE_STATES.OPEN)}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				sx={accountItemSX(isPanelOpen)}
				tabIndex={0}
				onKeyDown={handleOnKeyDown}
			>
				<Box sx={accountSX.containerMobile(theme)} id="accountPanelTrigger">
					<DynamicIcon
						name={MATERIAL_SYMBOL_ICONS.person}
						variant={isActive ? 'filled' : 'rounded'}
					/>
					<Typography sx={accountSX.label(theme)} fontSize={12}>
						{HeaderLabels.account.t()}
					</Typography>
				</Box>
			</Stack>
			<HeaderCommonPanel
				idPopper={idPopper}
				isOpen={isPanelOpen}
				anchorEl={anchorEl}
				handleClose={closePanel}
				sx={accountSX.panel(theme)}
			>
				<Stack direction="row">
					<AccountDropMenuPanel onClose={closePanel} />
				</Stack>
			</HeaderCommonPanel>
		</Box>
	);
};
